import React from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { CreateCollectiveAgreementRequest } from "../../../networking/collective-agreement.hooks";
import AppInputNew from "../../Core/AppInput/AppInputNew";
import AppSelect, { AppSelectController } from "../../Core/AppSelect/AppSelect";
import AppText from "../../Core/AppText/AppText";
import Button from "../../Core/Button/Button";
import TextAreaNew from "../../Core/TextArea/TextAreaNew";
import FloatingButtonPanel from "../FloatingButtonPanel/FloatingButtonPanel";
import MonthlyGuaranteedHoursTable from "./MonthlyGuaranteedHoursTable";

export interface CollectiveAgreementFormProps {
	form: UseFormReturn<CreateCollectiveAgreementRequest>;
	onSubmit: (data: CreateCollectiveAgreementRequest) => void | Promise<void>;
	isLoading?: boolean;
	submitButtonText?: string;
	initialValues?: Partial<CreateCollectiveAgreementRequest>;
}

const PENSION_TYPE_OPTIONS = [
	{ id: "itp1", name: "ITP1" },
	{ id: "itp2", name: "ITP2" },
	{ id: "notWhiteCollar", name: "Ej tjänsteman" },
];

const UNION_TYPE_OPTIONS = [
	{ id: "noAfa", name: "Ej AFA (inget kollektivavtal)" },
	{ id: "blueCollar", name: "Arbetare" },
	{ id: "whiteCollar", name: "Tjänsteman" },
];

export const EMPLOYMENT_TYPE_OPTIONS = [
	{ id: "fixedTerm", name: "Visstidsanställning" },
	{ id: "contractor", name: "Uppdragstagare" },
	{ id: "probation", name: "Provanställning" },
	{ id: "permanent", name: "Tillsvidare" },
	{ id: "subcontractor", name: "Underkonsult" },
];

const CollectiveAgreementForm: React.FC<CollectiveAgreementFormProps> = ({
	form,
	onSubmit,
	isLoading = false,
	submitButtonText = "Skapa kollektivavtal",
	// initialValues,
}) => {
	const { register, formState, handleSubmit, control, setValue, getValues } = form;

	const handleCopyToAllMonths = () => {
		// Get the value from January (month 1)
		const allValues = getValues();
		const januaryValue = allValues.guaranteedHoursByMonth?.[1];

		// Copy to all remaining months (2-12)
		if (januaryValue !== undefined) {
			for (let monthKey = 2; monthKey <= 12; monthKey++) {
				setValue(`guaranteedHoursByMonth.${monthKey}` as any, januaryValue, {
					shouldValidate: true,
					shouldDirty: true,
					shouldTouch: true,
				});
			}
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="grid grid-cols-[1fr,2px,1fr] gap-8 w-full max-w-[1250px] mx-auto pb-24">
				{/* Left column - Main form fields */}
				<div className="flex flex-col gap-8">
					<div className="flex flex-col gap-5">
						<AppText color="grayText" size="xm" weight="bold">
							{"Allmän information"}
						</AppText>

						<AppInputNew
							{...register("name")}
							label="Kollektivavtalets namn"
							placeholder="Internt namn"
							errors={formState.errors?.name?.message}
							mandatory
							showKnowledgeBaseForTagOnHover="collective-agreement-name"
						/>

						<TextAreaNew
							{...register("internalDescription")}
							label="Intern beskrivning"
							errors={formState.errors?.internalDescription?.message}
							width="100%"
							mandatory={false}
							showKnowledgeBaseForTagOnHover="internal-description"
						/>
						<div className="flex w-full gap-4">
							<AppInputNew
								{...register("productionUnit")}
								label="Resultatenhet"
								placeholder="Kostnadsställe löneprogram/ bokföring"
								errors={formState.errors?.productionUnit?.message}
								mandatory={false}
								showKnowledgeBaseForTagOnHover="production-unit"
							/>
							<AppInputNew
								{...register("salaryProgramCategory")}
								label="Urvalskategori löneprogrammet"
								placeholder="Urvalskategori i löneprogram"
								errors={formState.errors?.salaryProgramCategory?.message}
								mandatory={false}
								showKnowledgeBaseForTagOnHover="salary-program-category"
							/>
						</div>

						<div className="flex w-full gap-4">
							<AppSelectController
								control={control}
								name="unionType"
								label="Typ av kollektivavtal"
								placeholder="Välj typ av kollektivavtal"
								options={UNION_TYPE_OPTIONS}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
								errors={formState.errors?.unionType?.message}
								mandatory
								showKnowledgeBaseForTagOnHover="collective-agreement-type"
							/>
							<AppSelectController
								control={control}
								name="employmentType"
								label="Anställningsform"
								placeholder="Välj typ av anställning/ konsult"
								options={EMPLOYMENT_TYPE_OPTIONS}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
								errors={formState.errors?.employmentType?.message}
								mandatory
								showKnowledgeBaseForTagOnHover="employment-type"
							/>
						</div>
					</div>

					<div className="flex flex-col gap-5">
						<AppText color="grayText" size="xm" weight="bold">
							{"Statistik - SN, SCB"}
						</AppText>

						<div className="grid grid-cols-2 gap-4">
							<AppInputNew
								{...register("salaryStatistics.wageType")}
								label="Löneform"
								placeholder="En siffra"
								errors={formState.errors?.salaryStatistics?.wageType?.message}
								mandatory
								showKnowledgeBaseForTagOnHover="wage-type"
							/>
							<AppInputNew
								{...register("salaryStatistics.occupationCode")}
								label="Yrkeskod (SN)"
								placeholder="Sex siffror"
								errors={formState.errors?.salaryStatistics?.occupationCode?.message}
								mandatory={false}
								showKnowledgeBaseForTagOnHover="occupation-code"
							/>

							<AppInputNew
								{...register("salaryStatistics.personnelCategory")}
								label="Personalkategori (fackkategori)"
								placeholder="1"
								errors={formState.errors?.salaryStatistics?.personnelCategory?.message}
								mandatory={false}
								description="En siffra, 1=arbetare, 4-6=tjänsteman"
								showKnowledgeBaseForTagOnHover="personnel-category"
							/>
							<AppInputNew
								{...register("salaryStatistics.unionNumber")}
								label="Förbundsnummer"
								errors={formState.errors?.salaryStatistics?.unionNumber?.message}
								mandatory={false}
								description="Metall/Unionen/GS facket"
								showKnowledgeBaseForTagOnHover="union-number"
							/>

							<AppInputNew
								{...register("salaryStatistics.workTimeCode")}
								label="Arbetstidsart"
								errors={formState.errors?.salaryStatistics?.workTimeCode?.message}
								mandatory={false}
								showKnowledgeBaseForTagOnHover="work-time-code"
							/>

							<AppInputNew
								{...register("salaryStatistics.federalNumber")}
								label="Förbundsnummer"
								placeholder="00"
								errors={formState.errors?.salaryStatistics?.federalNumber?.message}
								mandatory={false}
								description="Två siffror"
								showKnowledgeBaseForTagOnHover="federal-number"
							/>
						</div>

						<div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
							<AppInputNew
								{...register("salaryStatistics.agreementCode")}
								label="Avtalskod"
								placeholder="000"
								errors={formState.errors?.salaryStatistics?.agreementCode?.message}
								mandatory={false}
								description="Tre siffror"
								showKnowledgeBaseForTagOnHover="agreement-code"
							/>
							<AppInputNew
								{...register("salaryStatistics.workplaceNumberSn")}
								label="Arbetsplatsnr (SN)"
								type="number"
								errors={formState.errors?.salaryStatistics?.workplaceNumberSn?.message}
								mandatory={false}
								description="Ange heltal"
								showKnowledgeBaseForTagOnHover="workplace-number-sn"
							/>
							<AppInputNew
								{...register("salaryStatistics.workplaceNumberScb")}
								label="Arbetsställenr (SCB)"
								errors={formState.errors?.salaryStatistics?.workplaceNumberScb?.message}
								mandatory={false}
								showKnowledgeBaseForTagOnHover="workplace-number-scb"
							/>
						</div>
					</div>
				</div>

				{/* Vertical divider */}
				<div className="bg-gray-200"></div>

				{/* Right column - Lön och arbetstid */}
				<div className="flex flex-col gap-8">
					<div className="flex flex-col gap-5">
						<AppText color="grayText" size="xm" weight="bold">
							{"Lön och arbetstid"}
						</AppText>

						<div className="grid grid-cols-2 gap-4">
							<AppInputNew
								{...register("vacationSalaryPercent", { valueAsNumber: true })}
								label="Semesterlön"
								placeholder="13"
								step="0.1"
								errors={formState.errors?.vacationSalaryPercent?.message}
								type="number"
								prefix="%"
								mandatory
								selectOnFocus
								description="Ange semesterlön i %"
								showKnowledgeBaseForTagOnHover="vacation-salary-percent"
							/>

							<Controller
								control={control}
								name="isGflControlled"
								render={({ field: { value, onChange, ...fieldProps } }) => (
									<AppSelect
										{...fieldProps}
										label="GFL – Genomsnittligt förtjänstläge"
										placeholder="Välj GFL-status"
										options={[
											{ id: "false", name: "Nej - konsultens timlön gäller alltid" },
											{ id: "true", name: "Ja - GFL tillämpas när det finns på uppdraget" },
										]}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
										errors={formState.errors?.isGflControlled?.message}
										mandatory
										value={value === true ? "true" : value === false ? "false" : undefined}
										onChange={(val) => onChange(val === "true")}
										showKnowledgeBaseForTagOnHover="gfl-controlled"
									/>
								)}
							/>
						</div>

						<AppInputNew
							{...register("guaranteeSalary", { valueAsNumber: true })}
							label="Garantilön/h"
							placeholder="Garantilön/h"
							errors={formState.errors?.guaranteeSalary?.message}
							type="number"
							prefix="SEK"
							mandatory
							selectOnFocus
							showKnowledgeBaseForTagOnHover="guarantee-salary"
						/>

						<MonthlyGuaranteedHoursTable
							control={control}
							errors={formState.errors}
							onCopyToAll={handleCopyToAllMonths}
						/>
					</div>
					<div className="flex flex-col gap-5">
						<AppText color="grayText" size="xm" weight="bold">
							{"Pension tjänsteman"}
						</AppText>

						<div className="flex w-full gap-4">
							<AppSelectController
								control={control}
								name="pensionType"
								label="Pension tjänsteman"
								placeholder="Välj typ av tjänstepension"
								options={PENSION_TYPE_OPTIONS}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
								errors={formState.errors?.pensionType?.message}
								mandatory
								showKnowledgeBaseForTagOnHover="pension-type"
							/>
							<AppInputNew
								{...register("itpCostCenter")}
								label="ITP kostnadsställe"
								placeholder="Kostnadsställe till Collectum"
								errors={formState.errors?.itpCostCenter?.message}
								mandatory={false}
								showKnowledgeBaseForTagOnHover="itp-cost-center"
							/>
						</div>

						<div className="flex w-1/2 gap-4">
							<AppInputNew
								{...register("itpPlanId")}
								label="ITP identifikation"
								placeholder="Pensionsplan till Collectum"
								errors={formState.errors?.itpPlanId?.message}
								mandatory={false}
								showKnowledgeBaseForTagOnHover="itp-plan-id"
							/>
						</div>
					</div>
				</div>
			</div>

			<FloatingButtonPanel transparent={false}>
				<Button type="submit" width="12.75rem" disabled={isLoading}>
					{submitButtonText}
				</Button>
			</FloatingButtonPanel>
		</form>
	);
};
export default CollectiveAgreementForm;
